import { orderBy, type QueryConstraint } from 'firebase/firestore';
import { type Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, take } from 'rxjs/operators';
import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../../abstract/Document';
import { Suppliers, type Supplier } from '../Suppliers';
import type { IBrochure } from './Brochures.types';

@Identifiable
@Timestampable
@Untrackable
export class Brochure extends AbstractDocument<IBrochure> {
  readonly collections = {};

  readonly translatable = (data: IBrochure): NonNullable<IBrochure['_translations']>[string] => ({
    name: data.name,
  });
}

export class Brochures extends AbstractCollection<Brochure, IBrochure> {
  static path = 'brochures';
  static readonly activeLimit = 6;

  static isActive(brochure: Pick<IBrochure, 'validUntil'>) {
    return !brochure.validUntil || brochure.validUntil.toMillis() > Date.now();
  }

  constructor(document: Supplier) {
    super(document.collection(Brochures.path), Brochure);
  }

  get supplier() {
    return Suppliers._.getById(this.reference.parent!.id);
  }

  /**
   * Fetches all brochures for the supplier, optionally filtered by type.
   */
  async getAll(): Promise<IBrochure[]> {
    return await this.observeAll().pipe(take(1)).toPromise();
  }

  /**
   * Returns true if the supplier has not yet reached the active brochure limit.
   */
  async isWithinActiveLimit(): Promise<boolean> {
    const brochures = await this.getAll();
    const activeBrochures = brochures.filter((b) => Brochures.isActive(b));
    return activeBrochures.length < Brochures.activeLimit;
  }

  /**
   * Observes all brochures for the supplier, optionally filtered by type.
   */
  observeAll(): Observable<IBrochure[]> {
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'asc')];

    return this.query(constraints)
      .observe(true)
      .pipe(
        map((brochures) => Object.values(brochures)),
        distinctUntilChanged(),
        shareReplay(1),
      );
  }
}
